// Per-customer CALL-OUT response-time (SLA) override section (Session ADQ), shown on the customer
// detail page for ADMINS only. It shows what this customer's call-outs are measured against and,
// when set, their negotiated override alongside it, with Add / Edit / Delete. A blank field falls
// back down the chain: this override -> the site's coverage zone -> the platform default.
// Self-contained: it fetches its own data and reports the active state up so the customer header can
// flag it with a pill. Deliberately mirrors CustomerRateOverrideSection (Session ADB).
//
// THE HONESTY POINT (locked): what a CLEARED field falls back to is read from zoneFallbacks - the
// zones this customer's sites actually sit in - never from `platform`. Telling an admin that
// clearing the attendance target reverts to the platform's 60 minutes would be a lie whenever their
// sites sit in a zone whose target is 240. The platform default is named only when the customer has
// no zone-placed site at all, which is the one case where it genuinely applies.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { SlaOverridePayload, SlaOverrideInput } from "../../lib/api";
import { Button, buttonClasses } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { ConfirmButton } from "../../components/ConfirmButton";
import { describeSlaFallback, formatSlaTarget, slaApiErrorText } from "../../lib/slaFormat";
import type { SlaTargetField } from "../../lib/slaFormat";

// A trimmed minutes string -> whole minutes, null when blank (fall back down the chain), or
// "invalid". The server enforces the real bounds (1 to 10080).
function minsOrNull(s: string): number | null | "invalid" {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) return "invalid";
  return n;
}

function minsToInput(mins: number | null): string {
  return mins == null ? "" : String(mins);
}

interface FormState {
  attendance: string;
  fix: string;
  entrapment: string;
  notes: string;
}

const EMPTY_FORM: FormState = { attendance: "", fix: "", entrapment: "", notes: "" };

const FIELD_INPUT =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";
const FIELD_LABEL = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function CustomerSlaOverrideSection({
  customerId,
  onActiveChange,
}: {
  customerId: string;
  onActiveChange?: (active: boolean) => void;
}) {
  const [payload, setPayload] = useState<SlaOverridePayload | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const override = payload?.override ?? null;
  const platform = payload?.platform ?? null;
  const fallbacks = payload?.zoneFallbacks ?? null;

  useEffect(() => {
    let live = true;
    setState("loading");
    api.customers.slaOverride
      .get(customerId)
      .then((p) => {
        if (!live) return;
        setPayload(p);
        setState("ready");
        onActiveChange?.(p.override != null);
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
    // onActiveChange is a stable setter; re-run only on customer change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  function startAdd() {
    setForm(EMPTY_FORM);
    setFormError(null);
    setEditing(true);
  }

  function startEdit() {
    if (!override) return;
    setForm({
      attendance: minsToInput(override.attendanceTargetMins),
      fix: minsToInput(override.fixTargetMins),
      entrapment: minsToInput(override.entrapmentAttendanceTargetMins),
      notes: override.notes ?? "",
    });
    setFormError(null);
    setEditing(true);
  }

  async function save() {
    const attendanceTargetMins = minsOrNull(form.attendance);
    const fixTargetMins = minsOrNull(form.fix);
    const entrapmentAttendanceTargetMins = minsOrNull(form.entrapment);
    if (
      attendanceTargetMins === "invalid" ||
      fixTargetMins === "invalid" ||
      entrapmentAttendanceTargetMins === "invalid"
    ) {
      setFormError("Enter each target as a whole number of minutes, or leave it blank to use the usual target.");
      return;
    }
    const body: SlaOverrideInput = {
      attendanceTargetMins,
      fixTargetMins,
      entrapmentAttendanceTargetMins,
      notes: form.notes.trim() === "" ? null : form.notes.trim(),
    };
    setSaving(true);
    setFormError(null);
    try {
      const result = override
        ? await api.customers.slaOverride.update(customerId, body)
        : await api.customers.slaOverride.create(customerId, body);
      // The write routes return the platform + override only, never zoneFallbacks, so the ones from
      // the GET are preserved rather than dropped (the fallback wording must survive a save).
      setPayload((p) => (p ? { ...p, ...result } : p));
      onActiveChange?.(result.override != null);
      setEditing(false);
    } catch (err) {
      setFormError(slaApiErrorText(err, "Could not save the response times. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setDeleting(true);
    try {
      await api.customers.slaOverride.remove(customerId);
      setPayload((p) => (p ? { ...p, override: null } : p));
      onActiveChange?.(false);
      setEditing(false);
      setFormError(null);
    } catch (err) {
      setFormError(slaApiErrorText(err, "Could not remove the response times. Please try again."));
    } finally {
      setDeleting(false);
    }
  }

  // One row: what this customer's call-outs are measured against, and where that figure came from.
  // With an override the negotiated figure wins and is pilled "Custom"; without one the row shows
  // the REAL fallback (the customer's zones), never the platform default unless it truly applies.
  function Row({ label, field, custom }: { label: string; field: SlaTargetField; custom: number | null }) {
    return (
      <div className="border-b border-ele-purple-accent py-2 last:border-0">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-ele-body">{label}</dt>
          <dd className="flex items-center gap-2 text-right">
            {custom != null ? (
              <>
                <span className="text-ele-strong">{formatSlaTarget(custom)}</span>
                <StatusPill word="Custom" tone="green" />
              </>
            ) : (
              <span className="text-ele-body">Not set</span>
            )}
          </dd>
        </div>
        {platform && fallbacks && (
          <p className="mt-0.5 text-xs text-ele-body opacity-80">{describeSlaFallback(field, fallbacks, platform)}</p>
        )}
      </div>
    );
  }

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-lg text-ele-blue">Response times</h2>
          {override != null && <StatusPill word="Active" tone="green" />}
        </div>
        {state === "ready" && !editing && (
          <div className="flex items-center gap-2">
            {override == null ? (
              <Button variant="secondary" size="sm" onClick={startAdd}>
                Add response times
              </Button>
            ) : (
              <>
                <button
                  onClick={startEdit}
                  className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                >
                  Edit
                </button>
                <ConfirmButton
                  onConfirm={remove}
                  busy={deleting}
                  label="Delete"
                  className={buttonClasses("destructive", "sm")}
                />
              </>
            )}
          </div>
        )}
      </div>

      <p className="mb-3 text-xs text-ele-body">
        How quickly ELE aims to respond to a call-out for this customer (for example a managing agent
        with an agreed response time). A blank field uses the target for the zone the site is in.
      </p>

      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" ? (
        <p role="alert" className="text-sm text-ele-danger">
          Could not load the response times. Please try again.
        </p>
      ) : !platform ? null : editing ? (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className={FIELD_LABEL}>Attend within (minutes)</span>
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={form.attendance}
                onChange={(e) => setForm((f) => ({ ...f, attendance: e.target.value }))}
                placeholder="Leave blank to use the zone target"
                className={FIELD_INPUT}
              />
            </label>
            <label className="block">
              <span className={FIELD_LABEL}>Fix within (minutes)</span>
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={form.fix}
                onChange={(e) => setForm((f) => ({ ...f, fix: e.target.value }))}
                placeholder="Leave blank to use the zone target"
                className={FIELD_INPUT}
              />
            </label>
            <label className="block">
              <span className={FIELD_LABEL}>Entrapment: attend within (minutes)</span>
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={form.entrapment}
                onChange={(e) => setForm((f) => ({ ...f, entrapment: e.target.value }))}
                placeholder="Leave blank to use the zone target"
                className={FIELD_INPUT}
              />
            </label>
          </div>
          <label className="block">
            <span className={FIELD_LABEL}>Notes (optional)</span>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="For example the agreement reference or who negotiated it."
              className={FIELD_INPUT}
            />
          </label>
          {formError && (
            <p role="alert" className="text-sm text-ele-danger">
              {formError}
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <button
              onClick={() => {
                setEditing(false);
                setFormError(null);
              }}
              disabled={saving}
              className="rounded-ele border border-ele-purple-accent px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <dl className="text-sm">
            <Row label="Attend within" field="attendance" custom={override?.attendanceTargetMins ?? null} />
            <Row label="Fix within" field="fix" custom={override?.fixTargetMins ?? null} />
            <Row label="Entrapment: attend within" field="entrapment" custom={override?.entrapmentAttendanceTargetMins ?? null} />
          </dl>
          {formError && (
            <p role="alert" className="mt-3 text-sm text-ele-danger">
              {formError}
            </p>
          )}
          {override == null ? (
            <p className="mt-3 text-xs text-ele-body">
              No agreed response times. This customer's call-outs are measured against the zone their
              site is in.
            </p>
          ) : override.notes && override.notes.trim() ? (
            <p className="mt-3 border-t border-ele-purple-accent pt-3 text-sm text-ele-body">{override.notes}</p>
          ) : null}
        </>
      )}
    </section>
  );
}
